import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PackageSearch, Smartphone, Monitor, ShoppingCart, Scissors } from 'lucide-react';
import { api } from '../../lib/api';
import { PainelTransferir } from './PainelTransferir';
import { PainelFracionar } from './PainelFracionar';

// REPOSICOES — a fila do estoquista: a vitrine chegou no minimo e ainda ha mercadoria no deposito.
//
// O problema em uma frase (palavras do Ruan): "acabar na canoa e perder venda, se o produto tem la
// no estoque". O produto esta a dez metros do cliente e ninguem sabe.
//
// A fila se enche SOZINHA pelo funcionamento normal da loja: a venda drena a prateleira principal
// primeiro e a mercadoria que chega pela nota cai sempre no lugar mais FUNDO ([DA-339]/[DA-356]).
// O deposito enche, a vitrine esvazia, e o minimo dispara. Ninguem alimenta nada.
//
// DUAS SECOES, e a separacao evita passeio perdido. Medido em prod ao construir: 14 produtos abaixo
// do minimo, mas so 8 com saldo em outro lugar. Pros outros 6 repor nao resolve — a resposta e
// comprar, e mandar o estoquista ate o deposito pra achar prateleira vazia gasta a confianca na
// fila mais rapido do que qualquer bug.

export interface Origem {
  localizacao_id: number;
  codigo: string;
  quantidade: string;
}

/** O pacote fechado do pai, quando o produto e filho de um fracionamento ativo ([DA-364]). */
export interface Fracionavel {
  frac_id: number;
  pai_id: number;
  pai_codigo: string | null;
  pai_descricao: string;
  pai_estoque: string;
  fator: string;
  /** As prateleiras onde o PACOTE esta (mesma forma das `origens`, mas do outro produto). */
  pai_lugares: Origem[];
}

export interface LinhaReposicao {
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  marca_nome: string | null;
  unidade_sigla: string | null;
  estoque_atual: string;
  vitrine_id: number;
  vitrine_codigo: string;
  vitrine_qtd: string;
  /** NULL desde a [DA-449]: a vitrine ZERADA entra na fila mesmo sem minimo definido. */
  vitrine_minimo: string | null;
  minimo_herdado: boolean;
  lugares: number;
  origens: Origem[];
  fracionavel: Fracionavel | null;
  saldo_deposito: number;
  sugerido: number;
  critico: boolean;
}

type Vista = 'mobile' | 'desktop';
const CHAVE_VISTA = 'fdc.reposicoes.vista';

/** Qual das tres respostas a secao esta dando: buscar no deposito, abrir pacote, ou comprar. */
type Modo = 'repor' | 'fracionar' | 'comprar';

const ROTULO_ORIGEM: Record<Modo, string> = {
  repor: 'Tem no deposito',
  fracionar: 'Tem no pacote fechado',
  comprar: 'Deposito',
};

const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = (v: unknown) => String(num(v)).replace('.', ',');

const ALERTA = { background: '#f97316', color: '#000', fontWeight: 700, borderRadius: 3, padding: '1px 6px' } as const;

export function Reposicoes() {
  const navigate = useNavigate();
  const [repor, setRepor] = useState<LinhaReposicao[]>([]);
  const [fracionar, setFracionar] = useState<LinhaReposicao[]>([]);
  const [comprar, setComprar] = useState<LinhaReposicao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [alvo, setAlvo] = useState<LinhaReposicao | null>(null);
  const [alvoFrac, setAlvoFrac] = useState<LinhaReposicao | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // A vista se escolhe sozinha na PRIMEIRA visita, pela largura: o tablet do estoquista abre em
  // Mobile e o PC do Ruan em Desktop, sem ninguem configurar nada. Depois disso manda a escolha
  // de cada um — a preferencia e por device, que e onde ela faz sentido.
  const [vista, setVista] = useState<Vista>(() => {
    const salvo = localStorage.getItem(CHAVE_VISTA);
    if (salvo === 'mobile' || salvo === 'desktop') return salvo;
    return window.innerWidth < 900 ? 'mobile' : 'desktop';
  });

  useEffect(() => { localStorage.setItem(CHAVE_VISTA, vista); }, [vista]);

  const carregar = useCallback(() => {
    setCarregando(true);
    api.get<{ repor: LinhaReposicao[]; fracionar: LinhaReposicao[]; comprar: LinhaReposicao[] }>('/api/reposicoes')
      .then((r) => { setRepor(r.repor ?? []); setFracionar(r.fracionar ?? []); setComprar(r.comprar ?? []); })
      .catch(() => { setRepor([]); setFracionar([]); setComprar([]); })
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  function avisar(msg: string) {
    setAviso(msg);
    carregar();
    window.setTimeout(() => setAviso(null), 4000);
  }

  function aoTransferir() {
    const nome = alvo?.codigo_interno ?? '';
    setAlvo(null);
    avisar(`Reposicao de ${nome} registrada.`);
  }

  function aoFracionar() {
    const nome = alvoFrac?.codigo_interno ?? '';
    const pai = alvoFrac?.fracionavel?.pai_codigo ?? '';
    setAlvoFrac(null);
    avisar(`Fracionamento de ${pai} para ${nome} registrado — o estoque dos dois produtos foi atualizado.`);
  }

  const criticos = repor.filter((l) => l.critico).length;

  // ── Peças compartilhadas pelas duas vistas ────────────────────────────────────────────────────
  // O botao de acao e o MESMO objeto nas duas: se um dia a vista Desktop parar de transferir, a
  // preferencia salva faria a tela abrir num beco ([DA-348]).
  const botaoRepor = (l: LinhaReposicao, largo: boolean) => (
    <button
      type="button"
      className="btn-primario"
      style={largo ? { width: '100%', padding: '12px 0', fontSize: 15 } : { flex: '0 0 auto' }}
      onClick={(e) => { e.stopPropagation(); setAlvo(l); }}
    >
      Repor
    </button>
  );

  // Botao SOLIDO, ao lado do Repor — controle de grade nasce com peso, nunca como texto colorido.
  // A tesoura e o mesmo icone da aba Fracionamento da ficha do produto: e a MESMA operacao, so que
  // alcancada da tela onde o problema aparece.
  const botaoFracionar = (l: LinhaReposicao, largo: boolean) =>
    l.fracionavel ? (
      <button
        type="button"
        className="btn-primario"
        title={`Abrir ${l.fracionavel.pai_codigo} ${l.fracionavel.pai_descricao} (tem ${fmt(l.fracionavel.pai_estoque)}) — 1 gera ${fmt(l.fracionavel.fator)}`}
        style={largo
          ? { width: '100%', padding: '12px 0', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }
          : { flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}
        onClick={(e) => { e.stopPropagation(); setAlvoFrac(l); }}
      >
        <Scissors size={largo ? 16 : 14} /> Fracionar
      </button>
    ) : null;

  const foto = (l: LinhaReposicao, grande: boolean) =>
    l.foto_url
      ? <img src={l.foto_url} alt="" className="produto-thumb" loading="lazy" style={grande ? { width: 56, height: 56 } : undefined} />
      : <div className="produto-thumb-vazio" style={grande ? { width: 56, height: 56 } : undefined} />;

  /** A vitrine em uma expressao: quanto tem contra quanto deveria. Zerada leva o realce laranja.
   *
   *  Sem minimo ([DA-449]) nao ha "de quanto" — a linha esta aqui porque **zerou**, nao porque
   *  cruzou um limite. Escrever "0 de 0" afirmaria que o minimo e zero, que e outra coisa (e faria
   *  a fila parecer errada). O texto diz o motivo real e o tooltip aponta o conserto. */
  const selVitrine = (l: LinhaReposicao) => (
    <span
      style={num(l.vitrine_qtd) <= 0 ? ALERTA : undefined}
      title={l.vitrine_minimo === null
        ? 'Sem minimo definido: esta na fila porque a vitrine zerou. Defina o Minimo de Compra do produto pra ser avisado ANTES de acabar.'
        : l.minimo_herdado ? 'Minimo herdado do Minimo de Compra do produto' : 'Minimo proprio desta prateleira'}
    >
      {l.vitrine_minimo === null
        ? `${fmt(l.vitrine_qtd)} — zerada (sem minimo)`
        : `${fmt(l.vitrine_qtd)} de ${fmt(l.vitrine_minimo)}`}
    </span>
  );

  const deposito = (l: LinhaReposicao) =>
    l.origens.map((o) => `${o.codigo}: ${fmt(o.quantidade)}`).join(' · ');

  /** O que a coluna de origem mostra em cada secao: prateleira, pacote fechado, ou nada. */
  const origemTexto = (l: LinhaReposicao, modo: Modo) => {
    if (modo === 'repor') return deposito(l);
    if (l.fracionavel) {
      return `${l.fracionavel.pai_codigo}: ${fmt(l.fracionavel.pai_estoque)} (1 gera ${fmt(l.fracionavel.fator)})`;
    }
    return <span className="muted">nada em outro lugar</span>;
  };

  // ── Vista MOBILE: cartao grande, botao de dedo ────────────────────────────────────────────────
  const cartao = (l: LinhaReposicao, modo: Modo) => (
    <div
      key={l.produto_id}
      style={{
        border: '1px solid var(--panel-border)',
        borderLeft: l.critico ? '5px solid #f97316' : '1px solid var(--panel-border)',
        borderRadius: 6,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        background: 'var(--panel-bg)',
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {foto(l, true)}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="text-gray-500 font-bold no-underline" style={{ fontSize: 12 }}>{l.codigo_interno}</div>
          <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.2 }}>{l.descricao}</div>
          {l.marca_nome && <div className="muted" style={{ fontSize: 12 }}>{l.marca_nome}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14 }}>
        <div>
          <div className="muted" style={{ fontSize: 11 }}>Vitrine {l.vitrine_codigo}</div>
          <div className="cel-num">{selVitrine(l)}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="muted" style={{ fontSize: 11 }}>{ROTULO_ORIGEM[modo]}</div>
          <div className="cel-num">{origemTexto(l, modo)}</div>
        </div>
      </div>
      {/* Numa linha que tem as DUAS saidas (prateleira e pacote do pai), o estoquista ve as duas e
          escolhe — repor de uma prateleira que ja esta contada custa uma caminhada; abrir um pacote
          e irreversivel na pratica. Por isso o Repor vem primeiro. */}
      {modo === 'repor' && botaoRepor(l, true)}
      {modo !== 'comprar' && botaoFracionar(l, true)}
      {modo === 'comprar' && (
        <button type="button" className="btn-secundario" style={{ width: '100%', padding: '10px 0' }} onClick={() => navigate(`/produtos/${l.produto_id}`)}>
          Abrir ficha
        </button>
      )}
    </div>
  );

  // ── Vista DESKTOP: tabela densa ───────────────────────────────────────────────────────────────
  const tabela = (linhas: LinhaReposicao[], modo: Modo) => {
    // Quando a secao pode mostrar DOIS botoes na mesma celula, a coluna precisa de largura propria.
    // `flex-wrap` nao pede espaco: a largura min-content de um container com wrap e a do maior item
    // sozinho, entao a tabela conclui que cabe e empilha os botoes um sobre o outro (mordeu na aba
    // Sessoes em 20/07). O `min-width` e a soma dos dois mais o gap.
    const doisBotoes = modo === 'repor' && linhas.some((l) => l.fracionavel);
    const largAcoes = doisBotoes ? 230 : 120;
    return (
      <table className="tabela">
        <thead>
          <tr>
            <th style={{ width: 60 }}>Foto</th>
            <th style={{ width: 80 }}>Codigo</th>
            <th>Descricao</th>
            <th style={{ width: 80 }}>Vitrine</th>
            <th style={{ width: 120, textAlign: 'right' }}>Tem / minimo</th>
            <th style={{ width: 220 }}>{ROTULO_ORIGEM[modo]}</th>
            <th style={{ width: largAcoes }}></th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={l.produto_id} onDoubleClick={() => navigate(`/produtos/${l.produto_id}`)}>
              <td>{foto(l, false)}</td>
              <td className="text-gray-500 font-bold no-underline">{l.codigo_interno}</td>
              <td>
                {l.descricao}
                {l.marca_nome && <div className="muted" style={{ fontSize: 11 }}>{l.marca_nome}</div>}
              </td>
              <td className="cel-num">{l.vitrine_codigo}</td>
              <td className="cel-num" style={{ textAlign: 'right' }}>{selVitrine(l)}</td>
              <td className="cel-num" style={{ fontSize: 12 }}>{origemTexto(l, modo)}</td>
              <td style={{ padding: 4 }}>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'stretch', flexWrap: 'nowrap', minWidth: largAcoes - 16 }}>
                  {modo === 'repor' && botaoRepor(l, false)}
                  {modo !== 'comprar' && botaoFracionar(l, false)}
                  {modo === 'comprar' && (
                    <button type="button" className="btn-secundario" onClick={() => navigate(`/produtos/${l.produto_id}`)}>Ficha</button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // O cartao tem teto de largura: o tablet em paisagem renderiza ~1200px CSS, e sem o teto o botao
  // Repor vira uma faixa de ponta a ponta — alvo de dedo nao precisa de 1.100px, e a linha de texto
  // longa demais fica ruim de ler. Alvo grande sim, faixa nao.
  const secao = (linhas: LinhaReposicao[], modo: Modo) =>
    vista === 'mobile'
      ? <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 820 }}>{linhas.map((l) => cartao(l, modo))}</div>
      : tabela(linhas, modo);

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <PackageSearch size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} /> Reposicoes
        </h2>
        {/* Alternador de vista: pedido do Ruan pra acompanhar do PC enquanto o estoquista usa o
            tablet. Botao SOLIDO nos dois estados (o inativo e o secundario da casa) — controle de
            grade nasce com peso, nao como texto colorido. */}
        <div className="pagina-acoes">
          <button
            type="button"
            className={vista === 'mobile' ? 'btn-primario' : 'btn-secundario'}
            onClick={() => setVista('mobile')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Smartphone size={15} /> Mobile
          </button>
          <button
            type="button"
            className={vista === 'desktop' ? 'btn-primario' : 'btn-secundario'}
            onClick={() => setVista('desktop')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Monitor size={15} /> Desktop
          </button>
        </div>
      </div>

      <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
        Produtos cuja <strong>vitrine</strong> (a prateleira com estrela, de onde a venda baixa) chegou
        no minimo. Quando ha mercadoria em outro lugar, o botao <strong>Repor</strong> transfere e
        registra de onde pra onde — o estoque total do produto <strong>nao muda</strong>. Quando o
        que existe e o <strong>pacote fechado</strong>, o botao <strong>Fracionar</strong> abre o
        pacote: ai o total do pai cai e o do filho sobe.
      </p>

      {aviso && (
        <p style={{ background: '#fde68a', color: '#000', padding: '8px 12px', borderRadius: 3, fontSize: 13 }}>{aviso}</p>
      )}

      {carregando ? <p className="muted">Carregando...</p> : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0 8px' }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>Repor da loja ({repor.length})</h3>
            {criticos > 0 && (
              <span style={ALERTA} title="Vitrine em zero com mercadoria no deposito — venda se perdendo agora">
                {criticos} sem nada na vitrine
              </span>
            )}
          </div>
          {repor.length === 0
            ? <p className="muted">Nenhuma vitrine no minimo com mercadoria pra repor.</p>
            : secao(repor, 'repor')}

          {/* A TERCEIRA secao ([DA-364]). Os 5 produtos filhos que estavam no minimo em 23/07 tinham
              UM LUGAR SO e por isso **nao apareciam em lugar nenhum** desta tela — o filtro da
              [DA-362] os descartava por "nunca teve pra onde repor", premissa que so vale enquanto a
              unica origem possivel e outra prateleira. Havia 6 pacotes de 200 parafusos no fundo. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '26px 0 8px' }}>
            <Scissors size={15} />
            <h3 style={{ margin: 0, fontSize: 15 }}>Abrir pacote ({fracionar.length})</h3>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
            Nao tem em outra prateleira, mas tem o <strong>pacote fechado</strong> no estoque. O
            botao <strong>Fracionar</strong> abre a quantidade que voce disser e joga as unidades
            neste produto — aqui o estoque total dos <strong>dois muda</strong>, ao contrario do
            Repor.
          </p>
          {fracionar.length === 0
            ? <p className="muted">Nada pra abrir.</p>
            : secao(fracionar, 'fracionar')}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '26px 0 8px' }}>
            <ShoppingCart size={15} />
            <h3 style={{ margin: 0, fontSize: 15 }}>Acabou — e compra ({comprar.length})</h3>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
            O produto tem <strong>outro lugar cadastrado, mas ele esta vazio</strong>, e nao ha
            pacote fechado pra abrir. Repor nao resolve — nao vale a caminhada ate o deposito. Quem
            tem uma prateleira so nunca teve pra onde repor e nao aparece aqui: e assunto da{' '}
            <strong>Lista de Compras</strong>.
          </p>
          {comprar.length === 0
            ? <p className="muted">Nada nesta situacao.</p>
            : secao(comprar, 'comprar')}
        </>
      )}

      <PainelTransferir linha={alvo} onFechar={() => setAlvo(null)} onPronto={aoTransferir} />
      <PainelFracionar linha={alvoFrac} onFechar={() => setAlvoFrac(null)} onPronto={aoFracionar} />
    </div>
  );
}
